import { Plus, Trash2, RefreshCw, ListOrdered, Pencil } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { api } from '@/lib/api';
import { getSocket } from '@/lib/socket';
import { prettyModelName, prettySequenceName } from '@/lib/utils';
import type { Annotation, ModelFile, Sequence, WebContentOverlay } from '@/lib/types';
import { SequenceEditor, newOverlayDraft, type OverlayDraft } from './SequenceEditor';
import { ModelThumbnail } from './ModelThumbnail';

interface DraftItem {
  model: string;
  externalURL: string;
  /** Uniform scale multiplier over the AR client's auto-fit. */
  scale: number;
  /** Live web content overlays on this model — see Asset.webContentOverlays. */
  overlays: OverlayDraft[];
  /** Per-sequence annotation override — see Asset.annotations. null/undefined = inherit the model's own defaults. */
  annotations?: Annotation[] | null;
}

/** Build the `webContentOverlays` payload for an API call — [] entries with an empty URL are dropped, [] -> null. */
function serializeOverlays(i: DraftItem): WebContentOverlay[] | null {
  const overlays = i.overlays
    .filter((o) => o.url.trim())
    .map((o) => ({
      url: o.url.trim(),
      position: o.position || '0 0 0',
      rotation: o.rotation || '0 0 0',
      width: o.width > 0 ? o.width : 1,
      height: o.height > 0 ? o.height : 0.6,
    }));
  return overlays.length ? overlays : null;
}

/** Inverse of serializeOverlays, for loading an existing sequence into draft state. */
function draftOverlaysFrom(overlays?: WebContentOverlay[] | null): OverlayDraft[] {
  return (overlays ?? []).map((o) => ({
    ...newOverlayDraft(),
    url: o.url,
    position: o.position ?? '0 0 0',
    rotation: o.rotation ?? '0 0 0',
    width: o.width ?? 1,
    height: o.height ?? 0.6,
  }));
}

/** Parse an A-Frame vec3 string ("s s s" or "sx sy sz") into a scalar. */
function parseScale(s?: string): number {
  if (!s) return 1;
  const first = parseFloat(String(s).trim().split(/\s+/)[0]);
  return Number.isFinite(first) && first > 0 ? first : 1;
}

/** Serialize a scalar back into the A-Frame vec3 the sync server stores. */
function serializeScale(n: number): string {
  const v = Number.isFinite(n) && n > 0 ? n : 1;
  return `${v} ${v} ${v}`;
}

export function SequencesPage() {
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [models, setModels] = useState<ModelFile[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [editing, setEditing] = useState<Sequence | null>(null);
  const [libraryFilter, setLibraryFilter] = useState('');
  const [libraryScrollRoot, setLibraryScrollRoot] = useState<HTMLDivElement | null>(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const [seqs, mdls] = await Promise.all([api.listSequences(), api.listModels()]);
      setSequences(seqs ?? []);
      setModels((mdls ?? []).filter((m) => m.name.toLowerCase().endsWith('.glb')));
    } catch (err) {
      toast.error('Failed to load sequences / models', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Refresh whenever the server broadcasts that a sequence changed.
  // Skip when we're actively editing so we don't clobber in-flight edits.
  const editingRef = useRef<Sequence | null>(editing);
  editingRef.current = editing;
  useEffect(() => {
    let stop: (() => void) | undefined;
    getSocket().then((s) => {
      const handler = () => {
        if (editingRef.current) return;
        load();
      };
      s.on('sequenceListChanged', handler);
      s.on('modelListChanged', handler);
      stop = () => {
        s.off('sequenceListChanged', handler);
        s.off('modelListChanged', handler);
      };
    });
    return () => stop?.();
  }, []);

  const filteredLibrary = useMemo(() => {
    const q = libraryFilter.trim().toLowerCase();
    return models.filter((m) => !q || m.name.toLowerCase().includes(q));
  }, [models, libraryFilter]);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>Sequences</CardTitle>
            <CardDescription>
              {sequences.length} sequence{sequences.length === 1 ? '' : 's'} · {models.length} model
              {models.length === 1 ? '' : 's'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Refresh"
              onClick={load}
              disabled={refreshing}
            >
              <RefreshCw className={refreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </Button>
            <Dialog open={creatorOpen} onOpenChange={setCreatorOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4" /> New sequence
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl">
                <DialogHeader>
                  <DialogTitle>New sequence</DialogTitle>
                  <DialogDescription>
                    Pick models, drag to reorder, and set an external URL per model.
                  </DialogDescription>
                </DialogHeader>
                <SequenceCreator
                  models={models}
                  existingFiles={sequences.map((s) => s.file)}
                  onSubmit={async (name, items) => {
                    try {
                      // 1. Create the sequence file (backend seeds default fields)
                      await api.createSequence(
                        name,
                        items.map((i) => i.model),
                      );
                      // 2. Immediately persist per-model settings entered in
                      //    the dialog (create endpoint doesn't accept them).
                      await api.updateSequence(
                        `${name}.json`,
                        items.map((i) => ({
                          model: i.model,
                          externalURL: i.externalURL,
                          scaling: serializeScale(i.scale),
                          webContentOverlays: serializeOverlays(i),
                          annotations: i.annotations ?? null,
                        })),
                      );
                      toast.success(`Created sequence "${name}"`);
                      setCreatorOpen(false);
                      await load();
                    } catch (err) {
                      toast.error('Create failed', {
                        description: err instanceof Error ? err.message : String(err),
                      });
                    }
                  }}
                />
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {sequences.length === 0 ? (
            <div className="grid place-items-center gap-2 px-6 py-10 text-center">
              <ListOrdered className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                No sequences yet. Create one to get started.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {sequences.map((s) => (
                <li
                  key={s.file}
                  className="group flex items-center gap-2 px-6 py-3 hover:bg-accent/40"
                >
                  <button
                    type="button"
                    onClick={() => setEditing(s)}
                    className="flex min-w-0 flex-1 flex-col text-left"
                  >
                    <span className="truncate text-sm font-medium">
                      {prettySequenceName(s.file)}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {s.data.assets.length} model
                      {s.data.assets.length === 1 ? '' : 's'} ·{' '}
                      {s.data.assets.slice(0, 3).map(prettyModel).join(', ')}
                      {s.data.assets.length > 3 && '…'}
                    </span>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Edit sequence"
                    onClick={() => setEditing(s)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete sequence"
                    onClick={async () => {
                      if (!confirm(`Delete sequence "${prettySequenceName(s.file)}"?`)) return;
                      try {
                        await api.deleteSequence(s.file);
                        toast.success('Sequence deleted');
                        await load();
                      } catch (err) {
                        toast.error('Delete failed', {
                          description: err instanceof Error ? err.message : String(err),
                        });
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* --- Sidebar model library (scrollable) ------------------ */}
      <Card className="flex max-h-[calc(100vh-8rem)] flex-col overflow-hidden">
        <CardHeader className="shrink-0 pb-3">
          <CardTitle className="text-base">Model library</CardTitle>
          <CardDescription>
            {libraryFilter
              ? `${filteredLibrary.length} of ${models.length} GLB${models.length === 1 ? '' : 's'}`
              : `${models.length} GLB${models.length === 1 ? '' : 's'} on the sync server.`}
          </CardDescription>
          <Input
            placeholder="Filter…"
            value={libraryFilter}
            onChange={(e) => setLibraryFilter(e.target.value)}
            className="mt-2"
          />
        </CardHeader>
        <CardContent className="min-h-0 flex-1 overflow-hidden p-0">
          <div ref={setLibraryScrollRoot} className="h-full overflow-y-auto p-6 pt-0">
            <div className="grid grid-cols-2 gap-2 pb-2">
              {filteredLibrary.map((m) => (
                <div
                  key={m.path}
                  className="overflow-hidden rounded-md border"
                  title={prettyModelName(m.path)}
                >
                  <ModelThumbnail
                    modelPath={m.path}
                    name={m.name}
                    size="sm"
                    scrollRoot={libraryScrollRoot}
                  />
                  <div className="border-t bg-muted/30 px-2 py-1 text-[10px]">
                    <div className="truncate font-medium">{prettyModelName(m.path)}</div>
                  </div>
                </div>
              ))}
              {filteredLibrary.length === 0 && (
                <div className="col-span-2 grid place-items-center py-6 text-xs text-muted-foreground">
                  No models match.
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* --- Edit dialog ------------------------------------------ */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Edit sequence</DialogTitle>
            <DialogDescription>
              Reorder, add or remove models, and edit the per-model external URL.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <EditController
              key={editing.file}
              sequence={editing}
              models={models}
              onSave={async (items) => {
                try {
                  await api.updateSequence(
                    editing.file,
                    items.map((i) => ({
                      model: i.model,
                      externalURL: i.externalURL,
                      scaling: serializeScale(i.scale),
                      webContentOverlays: serializeOverlays(i),
                      annotations: i.annotations ?? null,
                    })),
                  );
                  toast.success(`Saved "${prettySequenceName(editing.file)}"`);
                  setEditing(null);
                  await load();
                } catch (err) {
                  toast.error('Save failed', {
                    description: err instanceof Error ? err.message : String(err),
                  });
                }
              }}
              onCancel={() => setEditing(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function prettyModel(a: { model: string }) {
  return prettyModelName(a.model);
}

/** Wrapper that owns draft state for the create dialog. */
function SequenceCreator({
  models,
  existingFiles,
  onSubmit,
}: {
  models: ModelFile[];
  existingFiles: string[];
  onSubmit: (name: string, items: DraftItem[]) => void | Promise<void>;
}) {
  const [name, setName] = useState('');
  const [items, setItems] = useState<DraftItem[]>([]);

  const takenLower = useMemo(
    () =>
      new Set(
        existingFiles.map((f) => f.replace(/^.*\//, '').replace(/\.json$/i, '').toLowerCase()),
      ),
    [existingFiles],
  );
  const duplicate = !!name.trim() && takenLower.has(name.trim().toLowerCase());

  return (
    <>
      <SequenceEditor
        models={models}
        name={name}
        onNameChange={setName}
        items={items}
        onItemsChange={setItems}
      />
      {duplicate && (
        <p className="text-xs text-destructive">A sequence with this name already exists.</p>
      )}
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button
          disabled={!name.trim() || items.length === 0 || duplicate}
          onClick={() => void onSubmit(name.trim(), items)}
        >
          Create sequence
        </Button>
      </DialogFooter>
    </>
  );
}

/** Wrapper that owns draft state for the edit dialog. */
function EditController({
  sequence,
  models,
  onSave,
  onCancel,
}: {
  sequence: Sequence;
  models: ModelFile[];
  onSave: (items: DraftItem[]) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [items, setItems] = useState<DraftItem[]>(() =>
    sequence.data.assets.map((a) => ({
      model: a.model,
      externalURL: a.externalURL ?? '',
      scale: parseScale(a.scaling),
      overlays: draftOverlaysFrom(a.webContentOverlays),
      annotations: a.annotations ?? null,
    })),
  );
  const nameNoExt = prettySequenceName(sequence.file);

  // Normalizes an overlays list (either the server's WebContentOverlay[]
  // or a draft's OverlayDraft[] minus its client-only `id`) into a stable
  // JSON string for the dirty check — dropping empty-url entries and
  // filling in the same defaults serializeOverlays() would, so a loaded
  // sequence and its untouched draft always compare equal.
  const overlaysSig = (
    overlays: Array<{ url: string; position?: string; rotation?: string; width?: number; height?: number }> | null | undefined,
  ) =>
    JSON.stringify(
      (overlays ?? [])
        .filter((o) => o.url?.trim())
        .map((o) => ({
          url: o.url.trim(),
          position: o.position || '0 0 0',
          rotation: o.rotation || '0 0 0',
          width: o.width || 1,
          height: o.height || 0.6,
        })),
    );
  // null (inherit the model's defaults) and [] (explicitly override to
  // no annotations) are different states even though nothing else here
  // distinguishes them structurally, so the signature must too.
  const annotationsSig = (annotations: Annotation[] | null | undefined) =>
    annotations == null ? '∅' : JSON.stringify(annotations);
  const sig = (i: {
    model: string;
    externalURL?: string;
    scale: number;
    overlays: OverlayDraft[];
    annotations?: Annotation[] | null;
  }) =>
    `${i.model}|${i.externalURL ?? ''}|${i.scale}|${overlaysSig(i.overlays)}|${annotationsSig(i.annotations)}`;
  const initialSig = useMemo(
    () =>
      sequence.data.assets
        .map(
          (a) =>
            `${a.model}|${a.externalURL ?? ''}|${parseScale(a.scaling)}|${overlaysSig(a.webContentOverlays)}|${annotationsSig(a.annotations)}`,
        )
        .join('§'),
    [sequence],
  );
  const currentSig = items.map(sig).join('§');
  const dirty = initialSig !== currentSig;

  return (
    <>
      <SequenceEditor
        models={models}
        name={nameNoExt}
        onNameChange={() => {}}
        nameReadOnly
        items={items}
        onItemsChange={setItems}
      />
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!dirty || items.length === 0} onClick={() => void onSave(items)}>
          Save changes
        </Button>
      </DialogFooter>
    </>
  );
}
